import { ref } from "vue";
import { getAccountId } from "@/api/payRangeAPI";
import { getOperatingCurrency } from "@/api";
import { mapOperatingCurrencyRows } from "@/utils/operatingCurrencyMapper";
import { loadJobFamilyPickerResults } from "@components/JobFamilyPickerDialog/loadJobFamilyPickerResults";
import { usePayRangeCountryCatalog } from "@components/PayRangeModal/composables/usePayRangeCountryCatalog";
import { usePayRangeLocationSearch } from "@components/PayRangeModal/composables/usePayRangeLocationSearch";

const CURRENCY_PAGE_SIZE = 50;
const MAX_CURRENCY_PAGES = 10;

function toCurrencyPickerItem(row) {
  if (!row?.code) return null;
  return {
    ...row,
    name: row.name || row.code,
    operating_currency_id: row.operating_currency_id ?? row.id ?? null,
  };
}

export const DRAFT_DRAWER_WORK_MODE_OPTIONS = [
  { id: 1, name: "On-Site", code: "ON_SITE" },
  { id: 2, name: "Hybrid", code: "HYBRID" },
  { id: 3, name: "Remote", code: "REMOTE" },
  { id: 4, name: "Unspecified", code: "UNSPECIFIED" },
];

export const DRAFT_DRAWER_PAY_BASIS_OPTIONS = [
  { id: 1, name: "Hourly", code: "HOURLY" },
  { id: 2, name: "Monthly", code: "MONTHLY" },
  { id: 3, name: "Annually", code: "ANNUAL" },
];

/**
 * Picker lists the PayRangeDraftDrawer expects from its consumer (the drawer owns no data fetching).
 * Same sources as PayRangeModal: account operating countries and currencies, job-family
 * type-to-search, and geography type-to-search for region / sub-region.
 */
export function usePayRangeDraftDrawerOptions() {
  const jobFamilies = ref([]);
  const currencies = ref([]);
  const currencyLoading = ref(false);
  const location = ref(null);

  const { loadCatalog: loadCountries, locationCountryOptions } = usePayRangeCountryCatalog(
    () => [],
    getAccountId
  );

  const {
    regionItems,
    subRegionItems,
    locationSearchLoading,
    handleLocationSearch,
    resetSearchResults,
  } = usePayRangeLocationSearch({
    getLocation: () => location.value,
    setLocation: (next) => {
      location.value = next;
    },
  });

  let currencyLoadPromise = null;

  async function fetchCurrencies() {
    const accountId = getAccountId();
    if (!accountId) return;
    currencyLoading.value = true;
    try {
      const collected = [];
      let offset = 0;
      let total = null;
      for (let page = 0; page < MAX_CURRENCY_PAGES; page += 1) {
        const response = await getOperatingCurrency(accountId, {
          offset,
          size: CURRENCY_PAGE_SIZE,
          status: ["active"],
        });
        const data = response?.data;
        if (total === null) {
          const count = Number(data?.count);
          total = Number.isFinite(count) ? count : null;
        }
        const rows = Array.isArray(data?.results) ? data.results : [];
        collected.push(...rows);
        offset += rows.length;
        if (!rows.length || (total !== null && offset >= total)) break;
      }
      currencies.value = mapOperatingCurrencyRows(collected)
        .map(toCurrencyPickerItem)
        .filter(Boolean)
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    } catch {
      currencies.value = [];
    } finally {
      currencyLoading.value = false;
    }
  }

  function loadCurrencies() {
    if (currencies.value.length) return Promise.resolve();
    if (!currencyLoadPromise) {
      currencyLoadPromise = fetchCurrencies().finally(() => {
        currencyLoadPromise = null;
      });
    }
    return currencyLoadPromise;
  }

  async function loadCatalog() {
    await Promise.all([loadCountries(), loadCurrencies()]);
  }

  // The picker filters the loaded list client-side; opening it only needs the list to exist.
  async function searchCurrencies() {
    await loadCurrencies();
  }

  async function searchLocation(payload) {
    location.value = payload?.location ?? null;
    await handleLocationSearch(payload);
  }

  async function searchJobFamilies(query) {
    const accountId = getAccountId();
    if (!accountId) {
      jobFamilies.value = [];
      return;
    }
    try {
      jobFamilies.value = await loadJobFamilyPickerResults(accountId, query);
    } catch {
      jobFamilies.value = [];
    }
  }

  return {
    payBasisOptions: DRAFT_DRAWER_PAY_BASIS_OPTIONS,
    workModeOptions: DRAFT_DRAWER_WORK_MODE_OPTIONS,
    jobFamilies,
    countries: locationCountryOptions,
    currencies,
    currencyLoading,
    regionItems,
    subRegionItems,
    locationLoading: locationSearchLoading,
    loadCatalog,
    searchJobFamilies,
    searchCurrencies,
    searchLocation,
    resetLocationSearch: resetSearchResults,
  };
}
